import React from 'react';
import { Box, Typography, Chip, alpha, Avatar, Stack, useTheme } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { AIAnalysisResult } from '@features/analysis/types';

interface HeaderSectionProps {
  analysis: AIAnalysisResult | null;
  analyzing: boolean;
  loading: boolean;
  onAnalyze: () => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
  analysis,
  analyzing,
  loading,
  onAnalyze
}) => {
  const theme = useTheme();

  // Determine the current status
  const getStatusChip = () => {
    if (analyzing) {
      return (
        <Chip
          label="Analysis Running"
          color="primary"
          variant="filled"
          sx={{ fontWeight: 600, animation: 'pulse 1.5s infinite' }}
        />
      );
    } else if (analysis) {
      return (
        <Chip
          label="Analysis Complete"
          color="success"
          variant="filled"
          sx={{ fontWeight: 600 }}
        />
      );
    } else if (loading) {
      return (
        <Chip
          label="Loading Data"
          color="info"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      );
    } else {
      return (
        <Chip
          label="Ready to Analyze"
          color="default"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      );
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 4,
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2
    }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          sx={{
            bgcolor: theme.palette.primary.main,
            width: 56,
            height: 56,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <PsychologyIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            AI Property Analysis
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Powered by advanced machine learning algorithms
          </Typography>
        </Box>
      </Stack>

      {/* Status indicator instead of redundant button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {getStatusChip()}
        {analysis && (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
            Last updated:<br />
            {new Date().toLocaleDateString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default HeaderSection;